import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';
import { ModalService } from 'npx-family-happy-common';
import { catchError, debounceTime, map, of, Subject, takeUntil } from 'rxjs';
import { UserStorageService } from 'src/app/authentication/user-storage/user-storage.service';
import { CaregiverEvaluationRecord } from 'src/app/shared/models/caregiver-evaluation-record.model';
import {
  CaregiverEvaluation,
  DEFAULT_CAREGIVER_EVALUATION,
} from 'src/app/shared/models/caregiver-evaluation.model';
import { EvaluationService } from 'src/app/shared/services/evaluation-service/evaluation.service';
import {
  CoachState,
  RouterStateManagerService,
} from 'src/app/shared/services/router-state-manager/router-state-manager.service';

import { ImageCropperComponent } from 'src/app/shared/components/image-cropper/image-cropper.component';
import { AlertComponent } from 'src/app/shared/components/modals/alert/alert.component';
import { DEFAULT_CAREGIVER_ENGAGEMENT } from 'src/app/shared/models/caregiver-engagement.model';
import { cellFormatter, emailFormatter } from 'src/app/shared/utils.functions';
import { Coach } from '../../../shared/models/coach.model';
import { CoachService } from '../coach-service/coach-service.service';

// * coach specialties
// LIFE_COACHING,
// COACHING_AZIENDALE,
// EXECUTIVE_COACHING,
// COACHING_DI_LEADERSHIP,
// LEADERSHIP_COACHING,
// MINDFULNESS_COACHING,
// RELATIONSHIP_COACHING,
// SPIRITUAL_COACHING,
// COACHING_FOR_PERSONAL_GROWTH,
// CORPORATE_WELLNESS_COACHING,
// MENTAL_COACHING_PER_SPORTIVI_E_PERFORMANCE;
@Component({
  selector: 'app-coach-detail',
  templateUrl: './coach-detail.component.html',
  styleUrls: ['./coach-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertComponent,
    ImageCropperComponent,
  ],
})
export class CoachDetailComponent implements OnInit {
  isNew = true;
  hasPayed!: boolean;
  addressSelected: boolean = false;
  showAlert = false;
  showModal = false;
  chosenPhoto!: string;
  finalPhoto!: string;
  step = 0;
  form!: UntypedFormGroup;
  coach!: Coach;
  coachID!: string;
  generatePages: boolean = false;
  cvPages: number = 1;
  currentPage: number = 0;
  canvases: HTMLCanvasElement[] = [];
  checkNotRated = true;
  private nextTab = -1;
  private coachState!: CoachState;
  private unsubscribe = new Subject<void>();
  private caregiverRating: CaregiverEvaluation | undefined;
  private currentUser: string = '';
  private goBackRequested = false;

  //default option for NEW PSYCHOLOGIST
  defaultRating = DEFAULT_CAREGIVER_EVALUATION;
  defaultEngagement = DEFAULT_CAREGIVER_ENGAGEMENT;

  constructor(
    private caregiverEvaluationService: EvaluationService,
    private coachService: CoachService,
    private stateManager: RouterStateManagerService,
    private sanitizer: DomSanitizer,
    private modalService: ModalService,
    private activeRoute: ActivatedRoute,
    private userStorage: UserStorageService
  ) {
    this.finalPhoto = '';
  }

  ngOnInit(): void {
    this.userStorage
      .getUser()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((user: any) => {
        this.currentUser = user?.name ?? '';
      });
    this.form = new UntypedFormGroup({
      doc_ref: new UntypedFormControl(null),
      certificate: new UntypedFormControl(null),
      info: new UntypedFormGroup({
        name: new UntypedFormControl(null, Validators.required),
        surname: new UntypedFormControl(null, Validators.required),
        email: new UntypedFormControl(null, Validators.required),
        phone: new UntypedFormControl(null, Validators.required),
        fiscalcode: new UntypedFormControl(null),
        iban: new UntypedFormControl(null),
        age: new UntypedFormControl(0, Validators.min(0)),
        birthDate: new UntypedFormControl(null),
        resume: new UntypedFormControl(null),
        video: new UntypedFormControl(null),
        notes: new UntypedFormControl(null),
        resumeHidden: new UntypedFormControl(null),
        description: new UntypedFormControl(null),
        calendarLink: new UntypedFormControl(null),
        photo: new UntypedFormControl(null),
        stopWhatsapp: new UntypedFormControl(false),
        subscription: new UntypedFormControl(null),
        expDate: new UntypedFormControl(null),
        manualSubscription: new UntypedFormControl(null),
        expDateManual: new UntypedFormControl(null),
      }),

      lang: new UntypedFormControl(null),
      updateTimestamp: new UntypedFormControl(null),
      life_coaching: new UntypedFormControl(false),
      leadership_coaching: new UntypedFormControl(false),
      mindfulness_coaching: new UntypedFormControl(false),
      relationship_coaching: new UntypedFormControl(false),
      spiritual_coaching: new UntypedFormControl(false),
      coaching_for_personal_growth: new UntypedFormControl(false),
      executive_coaching: new UntypedFormControl(false),
      coaching_aziendale: new UntypedFormControl(false),
      corporate_wellness_coaching: new UntypedFormControl(false),
      coaching_di_leadership: new UntypedFormControl(false),
      mental_coaching_per_sportivi_e_performance: new UntypedFormControl(false),
    });
    this.activeRoute.params.subscribe((param) => {
      this.coachID = param.id;
    });
    try {
      this.stateManager
        .getCoachState()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((state: any) => {
          this.coachState = state;
          this.coachState.id = this.coachID;
          this.coachState.route = 'coach-detail/' + this.coachID;
          if (this.coachState.step >= 0) {
            this.step = this.coachState.step;
          }
          if (this.coachState.coach) {
            this.isNew = false;
            this.setCoach(this.coachState.coach);
            this.formInitialize(this.coachState.coach);
          } else if (!this.coachID.match('null')) {
            const id = this.coachID;
            if (id && id !== 'new') {
              this.isNew = false;
              this.coachService
                .getSingleCoach(id)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe((coaches) => {
                  let coach = coaches[0];
                  if (coach) {
                    this.setCoach(coach);
                    this.formInitialize(this.coach);
                  }
                });
            }
          }
        });
    } catch (err) {
      this.modalService.showErrorMessage(
        'Errore nel recupero delle informazioni legate al coach.'
      );
    }

    this.form.valueChanges
      .pipe(debounceTime(300), takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.coach = {
          docRef: value.doc_ref?.toString().trim() || null,
          certificate: value.certificate?.toString().trim() || null,
          info: {
            name: value.info.name?.toString().trim() || null,
            surname: value.info.surname?.toString().trim() || null,
            email: value.info.email?.toString().trim() || null,
            phone: value.info.phone?.toString().trim() || null,
            fiscalcode: value.info.fiscalcode?.toString().trim() || null,
            iban: value.info.iban?.toString().trim() || null,
            age: Number(value.info.age) || 0,
            birthDate: value.info.birthDate?.toString().trim() || null,
            resume: value.info.resume?.toString().trim() || null,
            video: value.info.video?.toString().trim() || null,
            notes: value.info.notes?.toString().trim() || null,
            resumeHidden: value.info.resumeHidden?.toString().trim() || null,
            description: value.info.description?.toString().trim() || null,
            calendarLink: value.info.calendarLink?.toString().trim() || null,
            photo: value.info.photo?.toString().trim() || null,
            stopWhatsapp: Boolean(value.info.stopWhatsapp),
            subscription: value.info.subscription?.toString().trim() || null,
            expDate: value.info.expDate?.toString().trim() || null,
            manualSubscription:
              value.info.manualSubscription?.toString().trim() || null,
            expDateManual: value.info.expDateManual?.toString().trim() || null,
          },
          lang: value.lang?.toString().trim() || null,
          updateTimestamp: value.updateTimestamp?.toString().trim() || null,
          specialties: [],
        };
      });
  }

  uploadFile(_event: any, field: string) {
    try {
      this.modalService.showLoadingWithMessage('Caricamento CV in corso...');
      const file = <File>_event.target.files[0];
      const path =
        field + '/' + field + this.form.get('info.email')?.value + '.pdf';
      let storageRef = ref(getStorage(), path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        'state_changed',
        () => {},
        () => {},
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            if (field === 'info.resume') {
              this.coach.info.resume = downloadURL;
            } else {
              this.coach.info.resumeHidden = downloadURL;
            }
            this.form.get(field)?.setValue(downloadURL);
            if (this.isNew) {
              this.createCoach();
              this.modalService.hideLoading();
              this.modalService.showSuccessMessage('Caricamento riuscito.');
            } else {
              this.updateCoach();
              this.modalService.hideLoading();
              this.modalService.showSuccessMessage('Caricamento riuscito.');
            }
          });
        }
      );
    } catch (err) {
      this.modalService.hideLoading();
      this.modalService.showErrorMessage(
        'Errore nel caricamento del CV. Si prega di riprovare'
      );
    }
  }

  uploadImage(image: string, id: string) {
    try {
      this.modalService.showLoadingWithMessage('Caricamento foto in corso...');
      const file = this.base64ToBlob(
        image.replace('data:image/png;base64,', ''),
        'image/png'
      );
      const path = 'pictures/' + id + '.png';
      let storageRef = ref(getStorage(), path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        'state_changed',
        () => {},
        () => {},
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            this.form.get('info.photo')?.setValue(downloadURL);
            let body = this.coach.info;
            body.photo = downloadURL;
            this.coach.info = body;
            if (this.isNew) {
              this.createCoach();
              this.modalService.hideLoading();
              this.modalService.showSuccessMessage('Caricamento riuscito.');
            } else {
              this.updateCoach();
              this.modalService.hideLoading();
              this.modalService.showSuccessMessage('Caricamento riuscito.');
            }
          });
        }
      );
    } catch (err) {
      this.modalService.hideLoading();
      this.modalService.showErrorMessage(
        'Errore nel corso del caricamento della foto. Si prega di riprovare.'
      );
    }
    this.showModal = false;
  }

  base64ToBlob(base64Data: string, contentType: string) {
    contentType = contentType || '';
    let sliceSize = 1024;
    let byteCharacters = atob(base64Data);
    let bytesLength = byteCharacters.length;
    let slicesCount = Math.ceil(bytesLength / sliceSize);
    let byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      let begin = sliceIndex * sliceSize;
      let end = Math.min(begin + sliceSize, bytesLength);

      let bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  goBack() {
    if (this.form.dirty) {
      this.showAlert = true;
      this.goBackRequested = true;
    } else {
      this.coachState.route = 'coach-list';
      this.coachState.coach = undefined;
      this.coachState.step = 0;
      this.coachState.id = 'null';
      this.ngOnDestroy();
      this.stateManager.setCoachState(this.coachState);
    }
  }

  formInitialize(coach: Coach) {
    if (coach) {
      this.form.setValue({
        doc_ref: coach.docRef,
        certificate: coach.certificate,
        info: {
          name: coach.info.name,
          surname: coach.info.surname,
          email: coach.info.email
            ? emailFormatter(coach.info.email as string)
            : null,
          phone: coach.info.phone
            ? cellFormatter(coach.info.phone as string)
            : null,
          fiscalcode: coach.info.fiscalcode,
          iban: coach.info.iban,
          age: coach.info.age,
          birthDate: coach.info.birthDate,
          resume: coach.info.resume,
          video: coach.info.video,
          notes: coach.info.notes,
          resumeHidden: coach.info.resumeHidden,
          description: coach.info.description,
          calendarLink: coach.info.calendarLink,
          photo: coach.info.photo,
          stopWhatsapp: coach.info.stopWhatsapp,
          subscription: coach.info.subscription,
          expDate: coach.info.expDate,
          manualSubscription: coach.info.manualSubscription,
          expDateManual: coach.info.expDateManual,
        },

        updateTimestamp: coach.updateTimestamp,
        life_coaching: coach.specialties.includes('LIFE_COACHING'),
        leadership_coaching: coach.specialties.includes('LEADERSHIP_COACHING'),
        mindfulness_coaching: coach.specialties.includes(
          'MINDFULNESS_COACHING'
        ),
        relationship_coaching: coach.specialties.includes(
          'RELATIONSHIP_COACHING'
        ),
        spiritual_coaching: coach.specialties.includes('SPIRITUAL_COACHING'),
        coaching_for_personal_growth: coach.specialties.includes(
          'COACHING_FOR_PERSONAL_GROWTH'
        ),
        executive_coaching: coach.specialties.includes('EXECUTIVE_COACHING'),
        coaching_aziendale: coach.specialties.includes('COACHING_AZIENDALE'),
        coaching_di_leadership: coach.specialties.includes(
          'COACHING_DI_LEADERSHIP'
        ),
        corporate_wellness_coaching: coach.specialties.includes(
          'CORPORATE_WELLNESS_COACHING'
        ),
        mental_coaching_per_sportivi_e_performance: coach.specialties.includes(
          'MENTAL_COACHING_PER_SPORTIVI_E_PERFORMANCE'
        ),
        lang: coach.lang,
        // {
        //   title: coach.lang.title,
        //   english: coach.lang.english,
        //   italian: coach.lang.italian,
        //   spanish: coach.lang.spanish,
        //   other1: coach.lang.other1,
        //   other2: coach.lang.other2,
        //   graduationType: coach.lang.graduationType,
        // },
      });
    }
  }

  save(showMessage?: boolean) {
    if (this.form.invalid) {
      const missing = this.getMissingFields();
      this.modalService.showErrorMessage(
        'Campi obbligatori mancanti o non validi: ' + missing.join(', ')
      );
      return;
    }
    if (this.isNew) {
      this.createCoach();
    } else {
      this.updateCoach(showMessage);
    }
  }

  getMissingFields(): string[] {
    const fields: string[] = [];
    const info = this.form.get('info');
    if (info?.get('name')?.invalid) fields.push('Nome');
    if (info?.get('surname')?.invalid) fields.push('Cognome');
    if (info?.get('email')?.invalid) fields.push('Email');
    if (info?.get('phone')?.invalid) fields.push('Cellulare');
    return fields;
  }

  changeTab(tab: number) {
    if (this.form.dirty) {
      this.showAlert = true;
      this.nextTab = tab;
    } else {
      this.step = tab;
      this.coachState.step = this.step;
    }
  }

  onAlertAction(save: boolean) {
    if (save && !this.isNew) {
      this.updateCoach();
    } else if (save && this.isNew) {
      this.createCoach();
    } else if (!save && this.goBackRequested) {
      if (this.coach === undefined) {
        this.form.markAsPristine();
        this.form.reset();
        this.goBack();
      } else {
        this.form.markAsPristine();
        this.form.reset();
        this.formInitialize(this.coach);
        this.goBack();
      }
    } else {
      this.form.markAsPristine();
      this.form.reset();
      this.formInitialize(this.coach);
      this.goBack();
    }
    this.showAlert = false;
    if (this.nextTab !== -1) {
      this.step = this.nextTab;
      this.coachState.step = this.step;
      this.nextTab = -1;
    }
  }

  private async createCoach() {
    this.coach = await this.processFormData(this.form.value);
    this.coachService
      .createCoach(this.coach)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (res: any) => {
          this.modalService.showSuccessMessage('Nuovo coach creato.');
          const regex = /#(\w{2}-\d{5})/gm;
          const result = regex.exec(res.message);
          if (result) {
            this.coachService
              .getSingleCoach(result[1])
              .pipe(takeUntil(this.unsubscribe))
              .subscribe((coaches) => {
                let coach = coaches[0];
                if (coach) {
                  this.isNew = !this.isNew;
                  this.coach = coach;
                  this.form.markAsPristine();
                  this.formInitialize(this.coach);
                }
              });
          }
        },
        error: (error) => {
          if ((error.error as string).match('Key .* already exists.')) {
            this.modalService.showErrorMessage(
              "Esiste già un profilo associato all'indirizzo email fornito."
            );
          } else {
            this.modalService.showErrorMessage(
              'Si è verificato un errore nella creazione della coach, si prega di riprovare.'
            );
          }
        },
      });
  }

  private async updateCoach(showMessage?: boolean) {
    this.coach = await this.processFormData(this.form.value);
    this.coachService
      .updateCoach(this.coach)
      .pipe(
        takeUntil(this.unsubscribe),
        map(() => {
          this.publishNewCaregiverScoreUpdate();
          this.form.markAsPristine();
          this.formInitialize(this.coach);
          if (showMessage) {
            this.modalService.showSuccessMessage('coach modificato.');
          }
        }),
        catchError((error) => {
          this.modalService.showErrorMessage(
            `Si è verificato un errore nell'aggiornamento del coach.`
          );
          return of();
        })
      )
      .subscribe();
  }

  private async processFormData(data: any) {
    const processedSpecialties: string[] = [];

    if (data.mental_coaching_per_sportivi_e_performance) {
      processedSpecialties.push('MENTAL_COACHING_PER_SPORTIVI_E_PERFORMANCE');
    }
    if (data.coaching_aziendale) {
      processedSpecialties.push('COACHING_AZIENDALE');
    }
    if (data.executive_coaching) {
      processedSpecialties.push('EXECUTIVE_COACHING');
    }
    if (data.corporate_wellness_coaching) {
      processedSpecialties.push('CORPORATE_WELLNESS_COACHING');
    }
    if (data.coaching_di_leadership) {
      processedSpecialties.push('COACHING_DI_LEADERSHIP');
    }
    if (data.life_coaching) {
      processedSpecialties.push('LIFE_COACHING');
    }
    if (data.leadership_coaching) {
      processedSpecialties.push('LEADERSHIP_COACHING');
    }
    if (data.mindfulness_coaching) {
      processedSpecialties.push('MINDFULNESS_COACHING');
    }
    if (data.relationship_coaching) {
      processedSpecialties.push('RELATIONSHIP_COACHING');
    }
    if (data.spiritual_coaching) {
      processedSpecialties.push('SPIRITUAL_COACHING');
    }
    if (data.coaching_for_personal_growth) {
      processedSpecialties.push('COACHING_FOR_PERSONAL_GROWTH');
    }

    let coach: Coach = {
      docRef: data.doc_ref?.toString().trim() || null,
      certificate: data.certificate?.toString().trim() || null,
      info: {
        name: data.info.name?.toString().trim() || null,
        surname: data.info.surname?.toString().trim() || null,
        email: data.info.email?.toString().trim() || null,
        phone: data.info.phone?.toString().trim() || null,
        fiscalcode: data.info.fiscalcode?.toString().trim() || null,
        iban: data.info.iban?.toString().trim() || null,
        age: Number(data.info.age) || 0,
        birthDate: data.info.birthDate?.toString().trim() || null,
        resume: data.info.resume?.toString().trim() || null,
        video: data.info.video?.toString().trim() || null,
        notes: data.info.notes?.toString().trim() || null,
        resumeHidden: data.info.resumeHidden?.toString().trim() || null,
        description: data.info.description?.toString().trim() || null,
        calendarLink: data.info.calendarLink?.toString().trim() || null,
        photo: data.info.photo?.toString().trim() || null,
        stopWhatsapp: Boolean(data.info.stopWhatsapp),
        subscription: data.info.subscription?.toString().trim() || null,
        expDate: data.info.expDate?.toString().trim() || null,
        manualSubscription:
          data.info.manualSubscription?.toString().trim() || null,
        expDateManual: data.info.expDateManual?.toString().trim() || null,
      },
      lang: data.lang?.toString().trim() || null,
      updateTimestamp: new Date().toISOString(),
      specialties: processedSpecialties,
    };
    return coach;
  }

  private setCoach(bb: Coach) {
    this.coach = bb;
    this.coachState.coach = this.coach;
    this.coachState.step = this.step;
  }

  openModal(event: any) {
    let image = event.target.files[0];
    this.chosenPhoto = URL.createObjectURL(image);
    this.showModal = true;
  }

  sanitize(url: string) {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  dismiss(event: Event) {
    const elem = event.target as HTMLElement;
    if (elem === document.getElementById('modal-container')) {
      this.showModal = false;
    }
  }

  getCroppedPhoto(image: string) {
    this.finalPhoto = image;
  }

  cropperAbort() {
    let imageInput = <HTMLInputElement>(
      document.getElementById('image-selection')
    );
    imageInput.value = '';
    this.showModal = false;
  }

  htmlToCanvas(showAlert: boolean) {
    if (this.currentPage >= this.cvPages) {
      this.currentPage = 0;
      this.generatePages = false;
      return;
    }
    const element = document.getElementById('cv-page-container') as HTMLElement;
  }

  onCaregiverEvaluationChanged(newCaregiverEvaluation: CaregiverEvaluation) {
    this.caregiverRating = newCaregiverEvaluation;
    this.form.markAsTouched();
    this.form.markAsDirty();
  }

  private publishNewCaregiverScoreUpdate() {
    if (this.caregiverRating) {
      // Insert code to save new caregiver evaluation in the database
      const record: CaregiverEvaluationRecord = {
        ...this.caregiverRating,
        id: null,
        docRef: this.coach.docRef,
        operator: this.currentUser,
        timestamp: new Date(),
      };
      this.caregiverRating = undefined;
      this.caregiverEvaluationService
        .addRecord(record)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.stateManager.setCoachState(this.coachState, false);
    this.processFormData(this.form.value).then((res) => {
      this.setCoach(res);
    });
  }
}
